import React, { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Save, X, Search, Link, Image, Video, Settings, Users, FileText } from 'lucide-react'
import { tenantTemplateAPI, tenantAPI, templateAPI } from '../services/api'

const TenantTemplateManagement = () => {
  const [tenantTemplates, setTenantTemplates] = useState([])
  const [tenants, setTenants] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAssignForm, setShowAssignForm] = useState(false)
  const [editingMapping, setEditingMapping] = useState(null)
  const [selectedTenant, setSelectedTenant] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [tenantFilter, setTenantFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('')
  const [formData, setFormData] = useState({
    tenantId: '',
    template_id: '',
    customName: '',
    customDescription: '',
    thumbnailUrl: '',
    sampleUrl: '',
    sampleVideoUrl: '',
    isActive: true,
    isPremium: false,
    is_public: true,
    customPrice: '',
    customCategory: '',
    customTags: [],
    notes: ''
  })

  const [errors, setErrors] = useState({})

  // Load data
  useEffect(() => {
    fetchTenantTemplates()
    fetchTenants()
    fetchTemplates()
  }, [searchTerm, tenantFilter, activeFilter])

  const fetchTenantTemplates = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {}
      if (tenantFilter) params.tenantId = tenantFilter
      if (activeFilter) params.isActive = activeFilter
      
      const response = await tenantTemplateAPI.getAll(params)
      
      if (response.data.success) {
        setTenantTemplates(response.data.data || [])
      } else {
        setError(response.data.message || 'Failed to fetch tenant-template mappings')
      }
    } catch (error) {
      console.error('Error fetching tenant-template mappings:', error)
      setError('Failed to load tenant-template mappings')
    } finally {
      setLoading(false)
    }
  }

  const fetchTenants = async () => {
    try {
      const response = await tenantAPI.getAll()
      if (response.data.success) {
        setTenants(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching tenants:', error)
    }
  }

  const fetchTemplates = async () => {
    try {
      const response = await templateAPI.getAll({ page: 1, limit: 200 })
      if (response.data.success) {
        setTemplates(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching templates:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.tenantId) newErrors.tenantId = 'Tenant is required'
    if (!formData.template_id) newErrors.template_id = 'Template is required'
    
    if (formData.thumbnailUrl && !/^https?:\/\/.+/.test(formData.thumbnailUrl)) {
      newErrors.thumbnailUrl = 'Thumbnail URL must be a valid URL'
    }
    
    if (formData.sampleUrl && !/^https?:\/\/.+/.test(formData.sampleUrl)) {
      newErrors.sampleUrl = 'Sample URL must be a valid URL'
    }
    
    if (formData.sampleVideoUrl && !/^https?:\/\/.+/.test(formData.sampleVideoUrl)) {
      newErrors.sampleVideoUrl = 'Sample video URL must be a valid URL'
    }
    
    if (formData.customPrice && (isNaN(formData.customPrice) || parseFloat(formData.customPrice) < 0)) {
      newErrors.customPrice = 'Custom price must be a valid positive number'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    try {
      setLoading(true)
      
      if (editingMapping) {
        const updateData = { ...formData }
        delete updateData.tenantId // Don't update tenant/template IDs
        delete updateData.template_id
        
        const response = await tenantTemplateAPI.update(editingMapping.id, updateData)
        
        if (response.data.success) {
          await fetchTenantTemplates()
          setEditingMapping(null)
          resetForm()
        } else {
          setError(response.data.message || 'Failed to update mapping')
        }
      } else {
        const response = await tenantTemplateAPI.assign(formData)
        
        if (response.data.success) {
          await fetchTenantTemplates()
          setShowAssignForm(false)
          resetForm()
        } else {
          setError(response.data.message || 'Failed to assign template')
        }
      }
    } catch (error) {
      console.error('Error saving mapping:', error)
      setError(error.response?.data?.message || 'Failed to save mapping')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (mapping) => {
    setEditingMapping(mapping)
    setFormData({
      tenantId: mapping.tenantId,
      template_id: mapping.template_id,
      customName: mapping.customName || '',
      customDescription: mapping.customDescription || '',
      thumbnailUrl: mapping.thumbnailUrl || '',
      sampleUrl: mapping.sampleUrl || '',
      sampleVideoUrl: mapping.sampleVideoUrl || '',
      isActive: mapping.isActive,
      isPremium: mapping.isPremium,
      is_public: mapping.is_public,
      customPrice: mapping.customPrice || '',
      customCategory: mapping.customCategory || '',
      customTags: mapping.customTags || [],
      notes: mapping.notes || ''
    })
    setShowAssignForm(true)
  }

  const handleDelete = async (mappingId) => {
    if (!window.confirm('Are you sure you want to remove this template from the tenant?')) return
    
    try {
      setLoading(true)
      const response = await tenantTemplateAPI.remove(mappingId)
      
      if (response.data.success) {
        await fetchTenantTemplates()
      } else {
        setError(response.data.message || 'Failed to remove mapping')
      }
    } catch (error) {
      console.error('Error removing mapping:', error)
      setError(error.response?.data?.message || 'Failed to remove mapping')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      tenantId: '',
      template_id: '',
      customName: '',
      customDescription: '',
      thumbnailUrl: '',
      sampleUrl: '',
      sampleVideoUrl: '',
      isActive: true,
      isPremium: false,
      is_public: true,
      customPrice: '',
      customCategory: '',
      customTags: [],
      notes: ''
    })
    setErrors({})
    setEditingMapping(null)
    setShowAssignForm(false)
  }

  const getStatusBadge = (isActive) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
    if (isActive) {
      return `${baseClasses} bg-green-100 text-green-800`
    }
    return `${baseClasses} bg-red-100 text-red-800`
  }

  const getPremiumBadge = (isPremium) => {
    if (!isPremium) return null
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Premium
      </span>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenant-Template Management</h1>
          <p className="text-gray-600">Manage template assignments and customizations for tenants</p>
        </div>
        <button
          onClick={() => setShowAssignForm(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Assign Template
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search mappings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Tenants</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
            ))}
          </select>
          
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          
          <button
            onClick={() => {
              setSearchTerm('')
              setTenantFilter('')
              setActiveFilter('')
            }}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <X className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Mappings Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading mappings...</span>
          </div>
        ) : tenantTemplates.length === 0 ? (
          <div className="text-center py-12">
            <Link className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No mappings found</h3>
            <p className="text-gray-600">Get started by assigning templates to tenants.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customizations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tenantTemplates.map((mapping) => (
                  <tr key={mapping.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {mapping.tenant?.name || 'Unknown Tenant'}
                          </div>
                          <div className="text-sm text-gray-500">{mapping.tenant?.domain}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-gray-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {mapping.customName || mapping.template?.name || 'Unknown Template'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {mapping.customCategory || mapping.template?.category}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-1">
                        {mapping.thumbnailUrl && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                            <Image className="w-3 h-3 mr-1" />
                            Thumb
                          </span>
                        )}
                        {mapping.sampleUrl && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                            <Image className="w-3 h-3 mr-1" />
                            Sample
                          </span>
                        )}
                        {mapping.sampleVideoUrl && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-purple-100 text-purple-800">
                            <Video className="w-3 h-3 mr-1" />
                            Video
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={getStatusBadge(mapping.isActive)}>
                          {mapping.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {getPremiumBadge(mapping.isPremium)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>Used: {mapping.usageCount || 0} times</div>
                      {mapping.lastUsedAt && (
                        <div className="text-xs">
                          Last: {new Date(mapping.lastUsedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short' })}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(mapping)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(mapping.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Assign/Edit Form Modal */}
      {showAssignForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingMapping ? 'Edit Template Assignment' : 'Assign Template to Tenant'}
                </h2>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tenant *
                    </label>
                    <select
                      name="tenantId"
                      value={formData.tenantId}
                      onChange={handleInputChange}
                      disabled={editingMapping}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.tenantId ? 'border-red-300' : 'border-gray-300'
                      } ${editingMapping ? 'bg-gray-100' : ''}`}
                    >
                      <option value="">Select Tenant</option>
                      {tenants.map(tenant => (
                        <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                      ))}
                    </select>
                    {errors.tenantId && (
                      <p className="text-red-500 text-xs mt-1">{errors.tenantId}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template *
                    </label>
                    <select
                      name="template_id"
                      value={formData.template_id}
                      onChange={handleInputChange}
                      disabled={editingMapping}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.template_id ? 'border-red-300' : 'border-gray-300'
                      } ${editingMapping ? 'bg-gray-100' : ''}`}
                    >
                      <option value="">Select Template</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>{template.name}</option>
                      ))}
                    </select>
                    {errors.template_id && (
                      <p className="text-red-500 text-xs mt-1">{errors.template_id}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Name
                    </label>
                    <input
                      type="text"
                      name="customName"
                      value={formData.customName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Override template name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Category
                    </label>
                    <input
                      type="text"
                      name="customCategory"
                      value={formData.customCategory}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Override template category"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Description
                  </label>
                  <textarea
                    name="customDescription"
                    value={formData.customDescription}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Override template description"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Thumbnail URL
                    </label>
                    <input
                      type="url"
                      name="thumbnailUrl"
                      value={formData.thumbnailUrl}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.thumbnailUrl ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="https://example.com/thumbnail.jpg"
                    />
                    {errors.thumbnailUrl && (
                      <p className="text-red-500 text-xs mt-1">{errors.thumbnailUrl}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sample URL
                    </label>
                    <input
                      type="url"
                      name="sampleUrl"
                      value={formData.sampleUrl}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.sampleUrl ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="https://example.com/sample.jpg"
                    />
                    {errors.sampleUrl && (
                      <p className="text-red-500 text-xs mt-1">{errors.sampleUrl}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sample Video URL
                    </label>
                    <input
                      type="url"
                      name="sampleVideoUrl"
                      value={formData.sampleVideoUrl}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.sampleVideoUrl ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="https://example.com/sample-video.mp4"
                    />
                    {errors.sampleVideoUrl && (
                      <p className="text-red-500 text-xs mt-1">{errors.sampleVideoUrl}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      name="customPrice"
                      value={formData.customPrice}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.customPrice ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                    {errors.customPrice && (
                      <p className="text-red-500 text-xs mt-1">{errors.customPrice}</p>
                    )}
                  </div>

                  <div className="flex items-end">
                    <div className="flex space-x-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700">
                          Active
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="isPremium"
                          checked={formData.isPremium}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700">
                          Premium
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="is_public"
                          checked={formData.is_public}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700">
                          Public
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Internal notes about this assignment"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Saving...' : editingMapping ? 'Update Assignment' : 'Assign Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TenantTemplateManagement
